// Teaches `theme.vars` to TypeScript. The theme is built with `cssVariables`,
// so every palette entry also exists as a CSS variable, which is how a board
// follows the color scheme without a `applyStyles` branch at each call site.
// The augmentation is global, so importing it here covers the whole app.
import { alpha } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";

import { colors, gray } from "./themePrimitives";

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    game: GameColors;
  }
  interface PaletteOptions {
    game?: GameColors;
  }
}

/**
 * What every in-house game agrees on, in both color schemes.
 *
 * Each game still owns its own felt and its own pieces - a mine is not a
 * diamond - but a win is the same green in all nineteen of them, and a meter's
 * empty half is the same grey. Those were copied into nineteen `*_COLORS`
 * objects and had started to drift: a loss was red in dice and a neutral grey
 * in keno, and a bright tile's dark ink was `#14100a` in two games and
 * `#1b1205` in a third.
 *
 * They are also the reason the games did not have a light mode. The boards
 * were written against a dark page - hairlines were `rgba(255,255,255,0.12)`,
 * tile faces were `layer2Dark`, text on the felt was white - so switching the
 * scheme left dark pieces on a white stage with invisible borders. Naming each
 * of those decisions once, per scheme, is what lets a board follow the toggle
 * without every game restating it.
 *
 * These are registered on `palette.game`, so a styled component reads them as
 * `theme.vars.palette.game.hairline` and gets a CSS variable that flips with
 * the scheme. Canvas boards cannot use a variable; they take the resolved set
 * from `useGameColors()` instead.
 */
export interface GameColors {
  /** The round went the player's way. Legible as ink and usable as a fill. */
  win: string;
  /** It did not. */
  lose: string;
  /** Nobody won: a blackjack push, a flower-poker tie. */
  push: string;

  /**
   * Ink for text sitting on a `win`/`lose`/brand fill.
   *
   * Dark in the dark scheme, where those fills are bright mint and sky blue; white
   * in the light one, where they are deep enough to need the opposite.
   */
  onAccent: string;

  /** The table a board is played on, when the game has no felt of its own. */
  felt: string;
  /** A piece on the felt: a covered tile, a reel cell, an empty slot. */
  piece: string;
  /** The edge of such a piece. */
  pieceEdge: string;

  /** The hairline that lifts a piece off the felt under it. */
  hairline: string;
  /** The empty half of any meter, reel or progress bar. */
  track: string;
  /** The shade a stage sits in, so a board has a floor rather than fading out. */
  shade: string;

  /** Text on the felt. */
  ink: string;
  /** A label beside it. */
  inkMuted: string;
  /** A round that paid nothing, a tile nobody reached. */
  inkFaint: string;

  /**
   * `win`/`lose` as bare `r g b` triples.
   *
   * `alpha()` cannot blend a CSS variable, and the banners and pips need these
   * at four different opacities. A channel token lets the call site write
   * `rgba(var(--…-winChannel) / 0.14)` and still follow the scheme.
   */
  winChannel: string;
  loseChannel: string;
  pushChannel: string;
  /** The same, for the blends that used to be `alpha("#ffffff", x)`. */
  inkChannel: string;
}

/** The outcomes a game can name without knowing what color they are. */
export type GameTone = "win" | "lose" | "push";

/** The scheme the games were designed in: a lit board in a dark room. */
export const gameColorsDark: GameColors = {
  win: colors.brightGreen,
  lose: colors.red,
  push: colors.nimbusCloud,

  onAccent: "#04101f",

  felt: colors.layer1Dark,
  piece: colors.layer2Dark,
  pieceEdge: alpha("#ffffff", 0.07),

  hairline: alpha("#ffffff", 0.12),
  track: alpha("#ffffff", 0.08),
  shade: alpha("#000000", 0.45),

  ink: "#ffffff",
  inkMuted: alpha("#ffffff", 0.55),
  inkFaint: alpha("#ffffff", 0.3),

  winChannel: "0 230 118",
  loseChannel: "207 32 46",
  pushChannel: "199 201 205",
  inkChannel: "255 255 255",
};

/**
 * The same board on a light page.
 *
 * Two things change beyond inverting the greys. The outcome colors go deeper -
 * a `#00E676` mint that reads beautifully on near-black is nearly invisible as
 * text on white - which in turn flips `onAccent` from near-black to white. And
 * the shade under a board is a tenth of its dark-scheme weight: the same 45%
 * black that reads as a table's floor on a dark page reads as a stain on a
 * light one.
 */
export const gameColorsLight: GameColors = {
  // Deep enough to carry text on white (~4.8:1) and to take white ink as a fill.
  win: "#0F7D56",
  lose: colors.red,
  push: gray[500],

  onAccent: colors.white,

  felt: gray[100],
  piece: colors.white,
  pieceEdge: alpha(gray[400], 0.4),

  hairline: alpha(gray[500], 0.28),
  track: alpha(gray[400], 0.3),
  shade: alpha(gray[700], 0.1),

  ink: gray[800],
  inkMuted: gray[600],
  inkFaint: alpha(gray[500], 0.6),

  winChannel: "15 125 86",
  loseChannel: "207 32 46",
  pushChannel: "62 82 115",
  inkChannel: "10 19 34",
};

/** Both schemes by name, for the places that resolve the mode themselves. */
export const gameColorsByMode = {
  light: gameColorsLight,
  dark: gameColorsDark,
} as const;
